import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Icon from "@mdi/react";
import {
  mdiAlertCircleOutline,
  mdiCalendarBlankOutline,
  mdiCalendarOutline,
} from "@mdi/js";
import {
  NylasDatePicker,
  NylasTimeslotPicker,
  NylasLocaleSwitch,
} from "@nylas/react";
import {
  setCurrentStep,
  setSelectedDate,
  setSelectedTimeslot,
  setSelectedTimezone,
  generateIdempotencyKey,
} from "../slice";
import { formatDate } from "../utils";

const StepSelectDateTime = () => {
  const dispatch = useDispatch();

  const {
    schedulingEnabled,
    availability,
    durationMinutes,
    selectedDate,
    selectedTimeslot,
    selectedTimezone,
  } = useSelector((state) => state.publicClientBooking);

  // Derive selectable dates from actual timeslot timestamps in the
  // selected timezone, so that e.g. LA Friday afternoon slots that
  // fall on Saturday in Taipei make Saturday selectable.
  const selectableDates = useMemo(() => {
    if (!availability) return [];
    const dateSet = new Set();
    Object.values(availability)
      .flat()
      .forEach((slot) => {
        const dateStr = new Date(slot.start_time).toLocaleDateString(
          "en-CA",
          { timeZone: selectedTimezone }
        );
        dateSet.add(dateStr);
      });
    return [...dateSet].map((dateStr) => new Date(dateStr + "T12:00:00Z"));
  }, [availability, selectedTimezone]);

  // Flatten availability into AvailabilityTimeslot[] for the timeslot picker
  // Nylas expects: { emails: string[], start_time: Date, end_time: Date }
  const flattenedAvailability = useMemo(() => {
    if (!availability) return [];
    return Object.values(availability)
      .flat()
      .map((slot) => ({
        emails: [],
        start_time: new Date(slot.start_time),
        end_time: new Date(slot.end_time),
      }));
  }, [availability]);

  const handleDateSelected = (event) => {
    const date = event.detail;
    if (!date) {
      dispatch(setSelectedDate(null));
      dispatch(setSelectedTimeslot(null));
      return;
    }
    // Extract YYYY-MM-DD in the selected timezone, then anchor to UTC noon
    // so the date stays stable across any browser timezone.
    const dateStr = (date instanceof Date ? date : new Date(date))
      .toLocaleDateString("en-CA", { timeZone: selectedTimezone });
    dispatch(setSelectedDate(dateStr + "T12:00:00Z"));
    // Clear timeslot when date changes
    dispatch(setSelectedTimeslot(null));
  };

  const handleTimeslotSelected = (event) => {
    const timeslot = event.detail;
    if (!timeslot) {
      dispatch(setSelectedTimeslot(null));
      return;
    }
    dispatch(
      setSelectedTimeslot({
        start_time:
          timeslot.start_time instanceof Date
            ? timeslot.start_time.toISOString()
            : timeslot.start_time,
        end_time:
          timeslot.end_time instanceof Date
            ? timeslot.end_time.toISOString()
            : timeslot.end_time,
      })
    );
  };

  const handleTimezoneChanged = (event) => {
    dispatch(setSelectedTimezone(event.detail));
  };

  // Check if scheduling is not enabled (availability was null from API)
  if (schedulingEnabled === false) {
    return (
      <div className="public-client-booking-form-container">
        <div className="public-client-booking-error-state">
          <Icon
            path={mdiAlertCircleOutline}
            size={3}
            className="public-client-booking-error-icon"
          />
          <h3 className="public-client-booking-error-title">
            Scheduling Not Available
          </h3>
          <p className="public-client-booking-error-message">
            Online scheduling is not currently available. Please contact the
            company directly to schedule your inspection.
          </p>
        </div>
      </div>
    );
  }

  // Check if no slots are available (availability is empty object)
  const hasAvailableSlots =
    availability && Object.keys(availability).length > 0;

  if (!hasAvailableSlots) {
    return (
      <div className="public-client-booking-form-container">
        <div className="public-client-booking-empty-state">
          <Icon
            path={mdiCalendarBlankOutline}
            size={3}
            className="public-client-booking-empty-icon"
          />
          <h3 className="public-client-booking-empty-title">
            No Available Time Slots
          </h3>
          <p className="public-client-booking-empty-message">
            There are no available time slots in the selected date range. Please
            check back later or contact the company directly.
          </p>
        </div>
      </div>
    );
  }

  // Availability is present - show Nylas date/time picker
  return (
    <div className="public-client-booking-nylas-container">
      {/* Top-Left: Title + Date Picker */}
      <div className="public-client-booking-nylas-left">
        <div className="public-client-booking-nylas-header">
          <h3 className="public-client-booking-nylas-title">Inspection</h3>
        </div>

        <div className="public-client-booking-nylas-datepicker">
          <NylasDatePicker
            selectableDates={selectableDates}
            selectedDate={selectedDate ? new Date(selectedDate) : undefined}
            selectedTimezone={selectedTimezone}
            eventDuration={durationMinutes}
            onDateSelected={handleDateSelected}
          />
        </div>
      </div>

      {/* Top-Right: Selected Date + Timeslot Picker */}
      <div className="public-client-booking-nylas-right">
        {selectedDate && (
          <div className="public-client-booking-nylas-selected-date">
            <Icon path={mdiCalendarOutline} size={0.75} />
            <span>{formatDate(selectedDate, selectedTimezone)}</span>
          </div>
        )}
        <div className="public-client-booking-nylas-timeslots">
          <NylasTimeslotPicker
            availability={flattenedAvailability}
            selectedDate={selectedDate ? new Date(selectedDate) : new Date()}
            selectedTimezone={selectedTimezone}
            onTimeslotSelected={handleTimeslotSelected}
          />
        </div>
      </div>

      {/* Bottom-Left: Locale Switch */}
      <div className="public-client-booking-nylas-locale">
        <NylasLocaleSwitch
          selectedTimezone={selectedTimezone}
          onTimezoneChanged={handleTimezoneChanged}
        />
      </div>

      {/* Bottom-Right: Next Button */}
      <div className="public-client-booking-nylas-footer">
        {selectedTimeslot && (
          <button
            type="button"
            className="btn btn-primary public-client-booking-next-btn"
            onClick={() => {
              dispatch(generateIdempotencyKey());
              dispatch(setCurrentStep(3));
            }}
          >
            Next
          </button>
        )}
      </div>
    </div>
  );
};

export default StepSelectDateTime;
